import { useState, useEffect, useMemo, useRef } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getClients } from '../../../shared/api/clients.api'
import type { Client } from '../../../shared/api/types'

/** Клиент в выпадашке: для показа хватает имени, кода и категории. */
export type ClientOption = Pick<Client, 'id' | 'name' | 'code' | 'category'> & {
  managerId?: string | null
}

interface Props {
  clientId: string
  /**
   * Выбранный клиент целиком: его может не быть среди загруженных (только что
   * созданный, из черновика, из заявки). Раньше имя искалось только в первых
   * 200 клиентах по алфавиту, и новый клиент за их пределами выглядел
   * невыбранным — его создавали заново (проверка Сергея 15.09.2026).
   */
  selectedClient: ClientOption | null
  onSelect: (client: ClientOption) => void
}

// Без ввода — первые клиенты по алфавиту, как раньше; поиск идёт на сервере
// по всем клиентам, доступным роли.
const LIST_LIMIT = 200
const SEARCH_LIMIT = 50
const SEARCH_DEBOUNCE_MS = 250

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

export function ClientCombobox({ clientId, selectedClient, onSelect }: Props) {
  const selected = selectedClient?.id === clientId ? selectedClient : null
  const [inputVal, setInputVal] = useState(selected?.name ?? '')
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const search = useDebouncedValue(open ? inputVal.trim() : '', SEARCH_DEBOUNCE_MS)
  const { data, isFetching } = useQuery({
    queryKey: ['clients', 'combobox', search],
    queryFn: () =>
      getClients({ search: search || undefined, limit: search ? SEARCH_LIMIT : LIST_LIMIT }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  // Поле показывает выбранного клиента; пока выпадашка открыта, ввод не трогаем.
  const selectedName = selected?.name ?? null
  useEffect(() => {
    if (open) return
    if (selectedName !== null) setInputVal(selectedName)
    else if (!clientId) setInputVal('')
  }, [open, selectedName, clientId])

  // Пока сервер не ответил на свежий ввод, сужаем прошлую выдачу сами — без мигания.
  const filtered = useMemo(() => {
    const items: ClientOption[] = data?.items ?? []
    const q = inputVal.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.code?.toLowerCase().includes(q) ?? false),
    )
  }, [data, inputVal])

  // Закрываем при клике вне компонента — имя выбранного вернёт эффект выше.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Скролл к выделенному пункту
  useEffect(() => {
    listRef.current?.children[highlighted]?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  const select = (client: ClientOption) => {
    onSelect(client)
    setInputVal(client.name)
    setOpen(false)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown') {
        setOpen(true)
        e.preventDefault()
      }
      return
    }
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1))
      e.preventDefault()
    }
    if (e.key === 'ArrowUp') {
      setHighlighted((h) => Math.max(h - 1, 0))
      e.preventDefault()
    }
    if (e.key === 'Enter' && filtered[highlighted]) {
      select(filtered[highlighted]!)
      e.preventDefault()
    }
  }

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <input
        value={inputVal}
        onChange={(e) => {
          setInputVal(e.target.value)
          setOpen(true)
          setHighlighted(0)
        }}
        onFocus={() => {
          setOpen(true)
          setHighlighted(0)
        }}
        onKeyDown={handleKey}
        placeholder="Поиск клиента..."
        autoComplete="off"
        className={`w-full px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
          selected ? 'border-brand-400 bg-brand-50/30' : 'border-gray-300'
        }`}
      />
      {selected && !open && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-mono pointer-events-none">
          [{selected.category}]
        </span>
      )}

      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto min-w-[340px]">
          <div ref={listRef}>
            {filtered.length === 0 ? (
              <div className="px-3 py-2.5 text-sm text-gray-400">
                {isFetching ? 'Поиск…' : 'Ничего не найдено'}
              </div>
            ) : (
              filtered.map((c, i) => (
                <div
                  key={c.id}
                  onMouseDown={() => select(c)}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-50 last:border-0 ${
                    i === highlighted ? 'bg-brand-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`flex-1 text-sm font-medium ${i === highlighted ? 'text-brand-800' : 'text-gray-900'}`}
                  >
                    {c.name}
                  </span>
                  {c.code && (
                    <span className="text-xs text-gray-400 font-mono shrink-0">{c.code}</span>
                  )}
                  <span
                    className={`text-xs font-semibold shrink-0 px-1.5 py-0.5 rounded ${
                      c.category === 'A'
                        ? 'bg-amber-100 text-amber-700'
                        : c.category === 'B'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {c.category}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
